<template>
  <transition name="app-lock-fade">
    <div v-if="value" class="pin-verify-overlay" @wheel.prevent @touchmove.prevent>
      <v-card flat color="transparent" class="pa-4" max-width="360" width="100%">
        <v-card-title class="text-h2">
          {{ displayTitle }}
        </v-card-title>
        <v-card-subtitle class="text-body-small mt-2">
          {{ displaySubtitle }}
        </v-card-subtitle>

        <v-card-text class="mt-4">
          <slot name="prepend" />
          <PinPad
            v-if="showPad !== false"
            ref="pinPad"
            :error="error"
            :disabled="busy"
            @complete="handleComplete"
          />
        </v-card-text>

        <v-card-actions v-if="showCancel !== false" class="pb-4 px-4">
          <v-btn text color="grey darken-1" :disabled="busy" @click="cancel">
            {{ vm.$t('profile.cAppSecurity.pin.setup.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref, computed, watch, getCurrentInstance } from 'vue';
import PinPad from './PinPad.vue';

const props = defineProps<{
  value: boolean;
  error?: string | null;
  busy?: boolean;
  title?: string;
  subtitle?: string;
  showCancel?: boolean;
  showPad?: boolean;
}>();

const emit = defineEmits<{
  (e: 'input', value: boolean): void;
  (e: 'confirm', pin: string): void;
}>();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const vm = getCurrentInstance()!.proxy as any;
const pinPad = ref<InstanceType<typeof PinPad> | null>(null);

const displayTitle = computed(() => props.title ?? vm.$t('profile.cAppSecurity.pin.verify.title'));
const displaySubtitle = computed(
  () => props.subtitle ?? vm.$t('profile.cAppSecurity.pin.verify.subtitle')
);

function handleComplete(pin: string) {
  emit('confirm', pin);
}

function cancel() {
  emit('input', false);
}

watch(
  () => props.error,
  err => {
    if (err) {
      pinPad.value?.reset();
    }
  }
);

watch(
  () => props.value,
  open => {
    if (!open) {
      pinPad.value?.reset();
    }
  }
);
</script>

<style scoped>
.pin-verify-overlay {
  position: fixed;
  inset: 0;
  z-index: 202;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  backdrop-filter: blur(12px);
  -webkit-font-smoothing: antialiased;
}

.pin-verify-overlay ::v-deep * {
  will-change: transform;
}

.app-lock-fade-enter-active,
.app-lock-fade-leave-active {
  transition: opacity 0.2s ease;
}
.app-lock-fade-enter,
.app-lock-fade-leave-to {
  opacity: 0;
}
</style>
